import {
  applyNodeChanges,
  type Edge,
  type Node,
  type NodeChange,
  type NodeTypes,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { _isNil } from '@/littledash';
import { type JSX, type PropsWithChildren, type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import styles from './GraphCanvas.module.scss';

const PRO_OPTIONS = { hideAttribution: true } as const;

interface GraphCanvasProps<TData extends Record<string, unknown>> {
  nodes: ReadonlyArray<Node<TData>>;
  edges: ReadonlyArray<Edge>;
  nodeTypes: NodeTypes;
}

/**
 * Inner canvas — must sit inside `ReactFlowProvider` to access `useReactFlow`.
 * Re-fits the graph whenever the container resizes (e.g. window resize, panel collapse).
 */
const GraphCanvasInner = <TData extends Record<string, unknown>>({
  nodes: engineNodes,
  edges,
  nodeTypes,
  children,
  containerRef,
}: PropsWithChildren<GraphCanvasProps<TData>> & {
  containerRef: RefObject<HTMLDivElement | null>;
}): JSX.Element => {
  const { fitView } = useReactFlow();
  const [nodes, setNodes] = useState<Array<Node<TData>>>(engineNodes as Array<Node<TData>>);

  // Sync when the engine publishes a new snapshot (reference changes after API hydration).
  useEffect(() => {
    setNodes(engineNodes as Array<Node<TData>>);
  }, [engineNodes]);

  // Required for controlled flows: applies dimension and initial position changes so fitView
  // calculates correctly. Select/remove events are dropped; read-only is enforced by nodesDraggable={false}.
  const onNodesChange = useCallback((changes: Array<NodeChange<Node<TData>>>) => {
    const initOnly = changes.filter((c) => c.type === 'dimensions' || c.type === 'position');
    if (initOnly.length > 0) {
      setNodes((nds) => applyNodeChanges(initOnly, nds));
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (_isNil(container)) {
      return;
    }
    const observer = new ResizeObserver(() => fitView({ duration: 200 }));
    observer.observe(container);
    return () => observer.disconnect();
  }, [fitView, containerRef]);

  // Re-fit when nodes are added (e.g. a streamed node) so a node laid out beyond the current viewport
  // isn't clipped. Keyed on node count — panning/zooming are disabled here, so re-fitting can't fight
  // the user. Deferred to the next frame so React Flow has measured the new node before fitting.
  const nodeCount = nodes.length;
  useEffect(() => {
    const frame = requestAnimationFrame(() => fitView({ duration: 200 }));
    return () => cancelAnimationFrame(frame);
  }, [nodeCount, fitView]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges as Array<Edge>}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      nodesDraggable={false}
      nodesConnectable={false}
      edgesReconnectable={false}
      elementsSelectable={false}
      panOnDrag={false}
      panOnScroll={false}
      zoomOnScroll={false}
      zoomOnPinch={false}
      zoomOnDoubleClick={false}
      fitView
      proOptions={PRO_OPTIONS}
    >
      {children}
    </ReactFlow>
  );
};

/**
 * Controlled, read-only React Flow canvas. The graph is seeded by the `nodes`/`edges` props; local
 * state handles dimension measurement internally so fitView works correctly. All interaction is disabled —
 * no dragging, connecting, selection, panning, or zoom. Fits to view on mount and re-fits on
 * container resize. Wraps its own `ReactFlowProvider` so it is drop-in anywhere.
 */
export const GraphCanvas = <TData extends Record<string, unknown>>(
  props: PropsWithChildren<GraphCanvasProps<TData>>
): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={containerRef} data-test-component="GraphCanvas" className={`${styles.canvas} bg-light-gray`}>
      <ReactFlowProvider>
        <GraphCanvasInner {...props} containerRef={containerRef} />
      </ReactFlowProvider>
    </div>
  );
};
